import * as React from 'react';
import { PureComponent } from 'react';
import area from '@turf/area';

interface ControlPanelProps {
  containerComponent: any;
  polygon: any;
  lngLat: [number, number] | null; // Change to array format
}

export default class ControlPanel extends PureComponent<ControlPanelProps> {
  // Memoize the area calculation to avoid recalculating on every render
  calculatePolygonArea = (polygon) => {
    if (!polygon) return null;

    try {
      let geoJsonInput = polygon;

      if (polygon.type === 'FeatureCollection') {
        geoJsonInput = polygon;
      } else if (polygon.type === 'Feature') {
        geoJsonInput = {
          type: 'FeatureCollection',
          features: [polygon],
        };
      } else if (polygon.type === 'Polygon' || polygon.type === 'MultiPolygon') {
        geoJsonInput = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: polygon,
            },
          ],
        };
      }

      if (geoJsonInput.type === 'FeatureCollection' && geoJsonInput.features) {
        const validFeatures = geoJsonInput.features.filter((feature) => {
          return (
            feature.geometry &&
            (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') &&
            feature.geometry.coordinates &&
            feature.geometry.coordinates.length > 0
          );
        });

        if (validFeatures.length === 0) {
          return null;
        }

        const cleanGeoJson = {
          type: 'FeatureCollection' as const,
          features: validFeatures,
        };

        return area(cleanGeoJson) / 1e6; // Convert to km²
      }

      return null;
    } catch (error) {
      console.error('Error calculating polygon area:', error);
      return null;
    }
  };

  render() {
    const { polygon, lngLat } = this.props;
    const defaultLngLat: [number, number] = [0, 0];

    // Handle array format directly
    const safeLngLat = lngLat || defaultLngLat;
    const [lng, lat] = safeLngLat;

    const polygonArea = this.calculatePolygonArea(polygon);

    // Format coordinates with proper cardinal directions
    const latDirection = lat >= 0 ? 'N' : 'S';
    const lngDirection = lng >= 0 ? 'E' : 'W';
    const formattedLat = Math.abs(lat).toFixed(4);
    const formattedLng = Math.abs(lng).toFixed(4);

    // Dynamic height based on polygon presence
    const baseHeight = 32;
    const expandedHeight = baseHeight * 3; // 3x the default height
    const currentHeight = polygon ? expandedHeight : baseHeight;

    return (
      <div
        className="control-panel"
        style={{
          width: '200px',
          height: `${currentHeight}px`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
          backgroundColor: '#455A64',
          color: '#FFFFFF', // Changed to pure white
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '4px',
          padding: '8px 12px',
          fontSize: '16px',
          fontFamily: 'Roboto',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          pointerEvents: 'none',
          transition: 'height 0.3s ease',
        }}
      >
        <p style={{ margin: '2px 0', color: '#FFFFFF', fontSize: '16px' }}>
          {lngLat
            ? `${formattedLat}°${latDirection}, ${formattedLng}°${lngDirection}`
            : 'Move mouse over map'}
        </p>
        {polygon && (
          <div>
            <p
              style={{
                margin: '2px 0 8px 0',
                color: '#FFFFFF', // Changed to pure white
                fontSize: '16px',
                letterSpacing: '0.5px',
              }}
            >
              Area:{' '}
              {polygonArea
                ? polygonArea > 1000
                  ? Math.round(polygonArea)
                  : polygonArea.toFixed(2)
                : 'N/A'}{' '}
              km²
            </p>
            <button
              onClick={() => {
                try {
                  navigator.clipboard.writeText(JSON.stringify(polygon, null, 2));
                } catch (error) {
                  console.error('Failed to copy polygon data:', error);
                }
              }}
              style={{
                backgroundColor: 'transparent',
                color: '#FFFFFF', // Changed to pure white
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '2px',
                padding: '2px 8px',
                fontSize: '12px',
                cursor: 'pointer',
                fontFamily: 'Roboto',
                fontWeight: '400',
                transition: 'all 0.2s ease',
                pointerEvents: 'auto',
                marginTop: '4px',
                marginBottom: '6px',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
            >
              Copy Polygon
            </button>
          </div>
        )}
      </div>
    );
  }
}
